import React, { useEffect, useState } from "react";

const loadingMessages = [
  "🥁 Dusting off the drum kit",
    "🎸 Tuning the guitars",
    "😴 Waking up the bassist",
    "🚫 Shooing the roadies off the stage",
    "🎵 Teaching the woodwind section to exhale",
    // "🦇 Chasing the bats away from Ozzy's green room",
    "💈 Adding styling gel to ZZ's beard",
    "🎷 Keeping the Sax-A-Boom on standby",
    "💔 Bracing Taylor's ex-boyfriends for new music",
];

const loadingIcons = [
  "/loading-icon-1.svg",
  "/loading-icon-2.svg",
  "/loading-icon-3.svg",
  "/loading-icon-4.svg",
];

// --- Shared State & Logic for Loading Messages ---
const _globallyShuffledMessages = [...loadingMessages].sort(() => Math.random() - 0.5);
let _sharedMessageIndex = 0;
let _sharedCurrentMessage = _globallyShuffledMessages.length > 0 ? "" : "No messages to display";
const _subscribers = new Set();
let _componentCounter = 0;
let _activeComponentIndex = 0;

let _letterIntervalId = null;
let _nextMessageTimeoutId = null;
let _isManagerActive = false;

function broadcastUpdate() {
  let componentIndex = 0;
  // Use setTimeout to batch the state updates for better performance
  setTimeout(() => {
    _subscribers.forEach(callback => {
      const isActive = componentIndex === _activeComponentIndex;
      callback(_sharedCurrentMessage, isActive);
      componentIndex++;
    });
  }, 0);
}

function typeNextSharedMessage() {
  if (_globallyShuffledMessages.length === 0) {
    _sharedCurrentMessage = "No messages to display.";
    broadcastUpdate();
    return;
  }

  clearInterval(_letterIntervalId);
  _letterIntervalId = null;
  clearTimeout(_nextMessageTimeoutId);
  _nextMessageTimeoutId = null;

  // Rotate to next active component
  _activeComponentIndex = (_activeComponentIndex + 1) % Math.max(1, _subscribers.size);
  
  _sharedCurrentMessage = ""; 

  const currentSentence = _globallyShuffledMessages[_sharedMessageIndex];
  if (typeof currentSentence !== 'string') {
      _sharedMessageIndex = (_sharedMessageIndex + 1) % _globallyShuffledMessages.length;
      typeNextSharedMessage();
      return;
  }

  let currentLength = 0;
  _letterIntervalId = setInterval(() => {
    currentLength++;
    _sharedCurrentMessage = currentSentence.slice(0, currentLength);
    broadcastUpdate();

    if (_sharedCurrentMessage.length === currentSentence.length) {
      clearInterval(_letterIntervalId);
      _letterIntervalId = null;
      _nextMessageTimeoutId = setTimeout(() => {
        _sharedMessageIndex = (_sharedMessageIndex + 1) % _globallyShuffledMessages.length;
        typeNextSharedMessage();
      }, 1000); // Pause after full message display
    }
  }, 120); // Increased from 80ms to 120ms for better performance
}

function startManager() {
  if (!_isManagerActive && _globallyShuffledMessages.length > 0) {
    _isManagerActive = true;
    typeNextSharedMessage();
  }
}

function stopManager() {
  if (_isManagerActive) {
    _isManagerActive = false;
    clearInterval(_letterIntervalId);
    _letterIntervalId = null;
    clearTimeout(_nextMessageTimeoutId);
    _nextMessageTimeoutId = null;
  }
}
// --- End Shared State & Logic ---

const LoadingComponent = () => {
  const [currentMessage, setCurrentMessage] = useState(_sharedCurrentMessage);
  const [loadingIconIndex, setLoadingIconIndex] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    // Subscribe to the shared message updates
    const handleUpdate = (newSharedMessage, componentIsActive) => {
      setCurrentMessage(newSharedMessage);
      setIsActive(componentIsActive);
    };
    _subscribers.add(handleUpdate);
    // Ensure component's currentMessage is up-to-date immediately
    setCurrentMessage(_sharedCurrentMessage);

    if (_subscribers.size === 1) { // First subscriber
      startManager();
    }

    return () => {
      _subscribers.delete(handleUpdate);
      if (_subscribers.size === 0) { // Last subscriber
        stopManager();
      }
    };
  }, []); // Runs once on mount, cleans up on unmount

  useEffect(() => {
    const iconInterval = setInterval(() => {
      setLoadingIconIndex((prev) => (prev + 1) % loadingIcons.length);
    }, 5000);

    // Use requestAnimationFrame for smoother percentage updates
    let animationId;
    let lastTime = 0;
    const animate = (time) => {
      if (time - lastTime >= 800) { // Update every 800ms instead of 500ms
        setPercentage((prev) => (prev + 1) % 101);
        lastTime = time;
      }
      animationId = requestAnimationFrame(animate);
    };
    animationId = requestAnimationFrame(animate);

    return () => {
      clearInterval(iconInterval);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  return (
    <div className="flex-1">
      <div className="[background:rgba(255,255,255,0.02)] relative border border-[#2c2a2c] rounded-lg p-[4px] md:max-w-[400px] w-full">
        <div className="absolute top-0 right-0 m-2">
          <div className="relative">
            <div
              style={{
                background:
                  "linear-gradient(135deg, rgba(252,16,242,0.90), rgba(48,120,228,0.90))",
              }}
              className="w-2 h-2 bg-blue-500 rounded-full animate-ping"
            ></div>
            <div
              style={{
                background:
                  "linear-gradient(135deg, rgba(252,16,242,0.90), rgba(48,120,228,0.90))",
              }}
              className="w-2 h-2 bg-blue-500 rounded-full absolute top-0 left-0"
            ></div>
          </div>
        </div>
        <div className="flex items-center">
          <div className="relative w-10 h-10 bg-[#232222] rounded-md flex items-center justify-center">
            <div className="inline-block h-9 w-9">
              <img
                src="/Loading.png"
                alt="Loading"
                className="animate-spin object-cover"
              />
            </div>
            {/* <div
              className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-solid border-current border-r-transparent border-[#c01eb8] align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
            >
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                Loading...
              </span>
            </div> */}
          </div>
          <div className="flex flex-col gap-4 animate-none">
            <p className={`font-light text-sm text-white ml-3 mr-3 ${!isActive ? 'opacity-30' : ''}`}>
              {isActive ? currentMessage : '◦ ◦ ◦'}
            </p>
            <div className="flex flex-col ml-3 space-y-1">
              <div className="w-36 absolute bottom-2 h-2 bg-[#3c393c] rounded-full overflow-hidden animate-pulse">
                <div className="absolute inset-0 bg-gradient-to-r from-[#3c393c] to-transparent animate-progress"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingComponent;
